import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { saveAs } from 'file-saver';

import { PageableTableComponent } from '../../shared/controls/pageable-table/pageable-table.component';
import { AutoCompleteDropdownComponent } from '../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { DialogService } from '../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../shared/toast/toast.service';
import { InitializerService } from '../../shared/services/initializer-service';
import { ErrorService } from '../../shared/services/error-service';
import { AuthService } from '../../shared/services/auth.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LoadingHelper } from '../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../shared/constants/area.constants';
import { PermissionConstants } from '../../shared/constants/permission.constants';

import { ExchangeRateService, PeriodService, DownloadService, FilteredExchangeRateItem, AllPeriodItem, PagingData } from '../../../service/proxy';

@Component({
    templateUrl: './exchange-rate.component.html',
    styleUrls: ['./exchange-rate.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        AutoCompleteDropdownComponent,
        HasPermissionDirective,
    ],

    providers: [
        ExchangeRateService,
        PeriodService,
        DownloadService,
    ]
})
export class ExchangeRateComponent implements OnInit {
    public intialized = false;

    public showEdit: boolean = false;
    public showCopy: boolean = false;
    public showDelete: boolean = false;

    public search: string = '';
    public paging: PagingData = new PagingData();
    public exchangeRates: FilteredExchangeRateItem[] = [];
    public periods: AllPeriodItem[] = [];
    public selectedPeriodId: number = 0;

    constructor(
        public authService: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private exchangeRateService: ExchangeRateService,
        private periodService: PeriodService,
        private downloadService: DownloadService,
        private dialogService: DialogService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private initializerService: InitializerService,
        private loadingService: LoadingService)
        {
            this.paging = initializerService.createPagingData();
        }

    ngOnInit(): void {
        this.initializePermissions();
        this.getAllPeriods();
    }

    initializePermissions(): void {
        this.showCopy = this.authService
            .hasPermission(AreaConstants.ExchangeRate, PermissionConstants.Create);

        this.showEdit = this.authService
            .hasPermission(AreaConstants.ExchangeRate, PermissionConstants.Edit);

        this.showDelete = this.authService
            .hasPermission(AreaConstants.ExchangeRate, PermissionConstants.Delete);
    }

    formatPeriod = (period: AllPeriodItem): string =>
        `${period?.year}-${String(period?.month).padStart(2, '0')}`;

    getAllPeriods() {
        LoadingHelper.withLoading(
            this.periodService.getAllPeriods(),
            this.loadingService
        ).subscribe((data) => {
            this.intialized = true;
            this.periods = data.results || [];
            this.selectedPeriodId = this.periods[0]?.id ?? 0;

            // If a date query parameter is present use it to select the period
            const dateParam = this.route.snapshot.queryParamMap.get('date');
            if (dateParam) {
                const parsed = new Date(dateParam);
                if (!isNaN(parsed.getTime())) {
                    const year = parsed.getFullYear();
                    const month = parsed.getMonth() + 1; // make 1-based to match period.month

                    const matchingFromDate = this.periods.find((p: any) => p.year === year && p.month === month);

                    this.selectedPeriodId = matchingFromDate?.id ?? this.periods[0]?.id ?? 0;
                    this.refreshGridData();

                    return;
                }
            }

            this.refreshGridData();
        });
    }

    onPeriodSelectionChange(periodId: number | null): void {
        this.selectedPeriodId = periodId ?? 0;
        this.refreshGridData();
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.getExchangeRates(this.paging);
    }

    getExchangeRates(pagingData: PagingData = new PagingData): void {
        if (!this.intialized || !this.selectedPeriodId) {
            return;
        }

        LoadingHelper.withLoading(
            this.exchangeRateService.getFilteredExchangeRates(this.selectedPeriodId.toString(), pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn),
            this.loadingService
        ).subscribe({
            next: (data) => {
                this.paging = data.pagingData;
                this.exchangeRates = data.results || [];
            },
            error: (error) => {
                if (error.status === 204) {
                    this.exchangeRates = [];
                    this.paging = this.initializerService.createPagingData();
                }
                else {
                    this.errorService.handleError(error);
                }
            }
        });
    }

    getColumnMap() {
        return {
            date: 'Date',
            exchangeRate: 'Exchange Rate',
            currency: 'Currency',
        };
    }

    addExchangeRate(): void {
        // determine date from selected period (fallback to today)
        const period = this.periods.find(p => p.id === this.selectedPeriodId);
        const pad = (n: number) => String(n).padStart(2, '0');
        let dateStr: string;

        if (period) {
            // assume period.month is 1-based
            dateStr = `${period.year}-${pad(period.month)}-01`;
        } else {
            const today = new Date();
            dateStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
        }

        this.router.navigate([`/exchange-rate/0`], { queryParams: { date: dateStr } });
    }

    editExchangeRate(exchangeRateId: number): void {
        this.router.navigate([`/exchange-rate/${exchangeRateId}`]);
    }

    copyExchangeRate(exchangeRateId: number): void {
        const period = this.periods.find(p => p.id === this.selectedPeriodId);
        const pad = (n: number) => String(n).padStart(2, '0');
        let dateStr: string;

        if (period) {
            const lastDay = new Date(period.year, period.month, 0);
            dateStr = `${lastDay.getFullYear()}-${pad(lastDay.getMonth() + 1)}-${pad(lastDay.getDate())}`;
        }
        else {
            const today = new Date();
            dateStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
        }

        LoadingHelper.withLoading(
            this.exchangeRateService.getExchangeRateForCopy(exchangeRateId),
            this.loadingService
        ).subscribe({
            next: (data) => {
                this.router.navigate([`/exchange-rate/0`], {
                    queryParams: {
                        currencyId: data.currencyId,
                        value: data.value,
                        date: dateStr
                    }
                });
            },
            error: (error) => {
                this.errorService.handleError(error);
            }
        });
    }

    deleteExchangeRate(exchangeRateId: number): void {
        this.dialogService.show('Delete', 'Are you sure you want to delete this record?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    LoadingHelper.withLoading(
                        this.exchangeRateService.deleteExchangeRate(exchangeRateId),
                        this.loadingService
                    ).subscribe({
                        next: () => {
                            this.getExchangeRates(this.paging);
                        },
                        error: (error: any) => {
                            this.errorService.handleError(error);
                        },
                        complete: () => {
                            this.toastService.show('Exchange rate deleted successfully.', 'success');
                        }
                    });
                }
            }
        );
    }

    downloadExchangeRates(pagingData: PagingData): void {
        if (!this.selectedPeriodId) {
            return;
        }

        LoadingHelper.withLoading(
            this.downloadService.downloadExchangeRates(this.selectedPeriodId.toString(), pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn),
            this.loadingService
        ).subscribe(response => {
            if (response.status === 200) {
                saveAs(response.data, 'Exchange_Rates.xlsx');
            }
            else {
                alert('Download failed');
            }
        });
    }
}
